import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Calendar, Trophy, Trash2, CheckCircle2, Copy, Sparkles, BadgeCheck, BookOpen, Image, Inbox, Save, MessageCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { AccountActions } from "../components/AccountActions";
import { HiringInbox } from "../components/Hiring";
import { Link } from "react-router-dom";
import { UploadBtn } from "../components/UploadBtn";
import PostComposer from "../components/PostComposer";
import { FollowersModal, PlanCard, ListingSwitcher } from "../components/InstituteWidgets";


const batchSummary = (b) => [b.timings, b.start_date && `starts ${b.start_date}`, b.pricing && `₹${b.pricing}`, (b.teachers || []).join(", "),
  (b.courses || []).length ? `Courses: ${b.courses.join(", ")}` : ""].filter(Boolean).join(" · ");

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function InstituteDashboard() {
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [student, setStudent] = useState({ name: "", phone: "", batch: "" });
  const [batch, setBatch] = useState({ name: "", status: "upcoming", timings: "", pricing: "", start_date: "", teachers: [], courses: [], description: "" });
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [course, setCourse] = useState({ title: "", fee: "", period: "Yearly" });
  const [galleryUrl, setGalleryUrl] = useState("");
  const [profile, setProfile] = useState(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const [listingId, setListingId] = useState(localStorage.getItem("listing-id") || "");
  const [site, setSite] = useState(null);

  const load = useCallback(() => {
    api.get("/settings/public").then((r) => setSite(r.data)).catch(() => {});
    api.get("/institute/dashboard").then((r) => {
      setData(r.data);
      setProfile({ phone: r.data.phone || "", whatsapp: r.data.whatsapp || "", description: r.data.description || "",
                   banner_url: r.data.banner_url || "", since_year: r.data.since_year || "", examsText: (r.data.exams || []).join(", "),
                   social_links: { facebook: "", instagram: "", youtube: "", twitter: "", linkedin: "", website: "", ...(r.data.social_links || {}) } });
      const si = r.data.school_info || {};
      setSchoolInfo({
        about: si.about || "", board: si.board || "", medium: si.medium || "", classes: si.classes || "",
        gender: si.gender || "", school_type: si.school_type || "", classrooms: si.classrooms || "",
        area_sqm: si.area_sqm || "", parent_institution: si.parent_institution || "", website: si.website || "",
        facilitiesText: (si.facilities || []).join(", "),
        feesText: (si.fees || []).map((f) => `${f.type} | ${f.frequency} | ${f.amount}`).join("\n"),
      });
    }).catch((e) => toast.error(apiError(e)));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, listingId]);
  useEffect(() => {
    const pid = sessionStorage.getItem("pending-package");
    if (!pid || !packages.length || !data || data.features?.plan !== "free") return;
    const p = packages.find((x) => x.id === pid);
    sessionStorage.removeItem("pending-package");
    if (p) { toast.info(`Complete payment for ${p.name} to activate your plan`); buyPackage(p); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages, data]);

  const act = async (fn) => {
    try {
      const res = await fn();
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const buyPackage = async (pkg) => {
    try {
      const { data: order } = await api.post("/payments/create-order", { package_id: pkg.id });
      if (order.mode === "razorpay") {
        const ok = await loadRazorpayScript();
        if (!ok) return toast.error("Could not load Razorpay checkout. Check your connection.");
        new window.Razorpay({
          key: order.key_id,
          amount: Math.round(order.amount * 100),
          currency: order.currency,
          name: order.business_name,
          description: order.package_name,
          order_id: order.order_id,
          handler: async (res) => {
            try {
              const { data: verify } = await api.post("/payments/verify", {
                order_id: order.order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              toast.success(verify.message);
              load();
            } catch (err) { toast.error(apiError(err)); }
          },
          theme: { color: "#A3E635" },
        }).open();
      } else {
        toast.info(`Demo payment: order ${order.order_id} for ₹${order.amount}. Simulating...`);
        const { data: verify } = await api.post("/payments/verify", { order_id: order.order_id });
        toast.success(verify.message);
        load();
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveSchoolInfo = () => {
    const { facilitiesText, feesText, ...rest } = schoolInfo;
    const payload = {
      ...rest,
      facilities: facilitiesText.split(",").map((s) => s.trim()).filter(Boolean),
      fees: feesText.split("\n").map((line) => {
        const [type, frequency, amount] = line.split("|").map((s) => (s || "").trim());
        return type ? { type, frequency: frequency || "", amount: amount || "" } : null;
      }).filter(Boolean),
    };
    act(() => api.put("/institute/school-info", payload));
  };

  const submitBatch = (e) => {
    e.preventDefault();
    act(() => api.post("/institute/batches", batch));
    setBatch({ name: "", status: "upcoming", timings: "", pricing: "", start_date: "", teachers: [], description: "" });
  };

  const copyReferral = () => {
    navigator.clipboard.writeText(data.referral_code).catch(() => {});
    toast.success("Referral code copied! You earn 100 points when another institute registers with it.");
  };

  if (!data) return <div className="p-16 text-center text-slate-500">Loading...</div>;

  const stats = [
    { label: "Rank Score", value: Math.round(data.total_score), bg: "#A3E635", testId: "institute-score-card" },
    { label: "Activity Points", value: data.points, bg: "#FCD34D" },
    { label: "Admissions", value: data.admissions, bg: "#FDBA74" },
    { label: "Followers", value: data.followers, bg: "#93C5FD", testId: "followers-stat", onClick: () => setShowFollowers(true) },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{data.name}</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            {data.area}, {data.city} · <span className="capitalize">{data.type}</span>
            {data.badge && (
              <span className="nb-badge" style={{ background: data.badge === "verified" ? "#93C5FD" : "#FCD34D" }}>
                {data.badge === "verified" ? <BadgeCheck className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />} {data.badge}
              </span>
            )}
          </p>
        </div>
        <button data-testid="institute-referral-btn" onClick={copyReferral} className="nb-btn nb-btn-sm">
          <Copy className="w-4 h-4" /> Referral: <span className="font-display">{data.referral_code}</span>
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map(({ label, value, bg, testId, onClick }) => (
          <div key={label} className="nb-stat" style={{ background: bg, cursor: onClick ? "pointer" : undefined }} data-testid={testId} onClick={onClick}>
            <Trophy className="w-5 h-5 mb-2" />
            <p className="font-display text-3xl font-extrabold">{value}</p>
            <p className="text-xs font-bold uppercase tracking-widest mt-1 opacity-70">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        <ListingSwitcher current={data.id} onSwitch={(id) => { localStorage.setItem("listing-id", id); setListingId(id); load(); }} />
        <PlanCard features={data.features} planExpires={data.plan_expires} />
      </div>
      {showFollowers && <FollowersModal onClose={() => setShowFollowers(false)} />}

      {data.features?.plan === "free" && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Choose a plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {packages.map((p) => (
              <div key={p.id} data-testid={`package-card-${p.id}`} className="nb p-6 flex flex-col">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h3 className="font-display text-lg font-extrabold">{p.name}</h3>
                </div>
                <p className="text-sm text-slate-500 mt-2">{p.description}</p>
                <p className="text-xs text-slate-600 mt-2 flex-1">{[p.featured && `Featured ${p.featured_days || p.duration_days} days`, `${p.posts_per_day} post${p.posts_per_day !== 1 ? "s" : ""}/day`, p.add_teachers && "Teachers", p.add_students && "Students", p.gallery && "Gallery", p.whatsapp && "WhatsApp", p.mcq && "MCQ tests", p.notes && "Notes", `${p.max_listings} listing${p.max_listings > 1 ? "s" : ""}`].filter(Boolean).join(" · ")}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-xl font-extrabold">₹{p.price}<span className="text-xs text-slate-500 font-normal"> / {p.duration_days} days</span></span>
                  <button data-testid={`package-buy-${p.id}`} onClick={() => buyPackage(p)} className="nb-btn nb-btn-lime nb-btn-sm">Buy Now</button>
                </div>
              </div>
            ))}
          </div>
          {site && (
            <p data-testid="purchase-agent-contact" className="text-xs text-slate-500 mt-2">
              For purchase assistance contact <span className="font-bold text-[#171B33]">{site.business_name}</span>
              {site.phone && <> · <a href={`tel:${site.phone}`} className="font-bold text-[#171B33] underline">{site.phone}</a></>}
            </p>
          )}
        </section>
      )}

      <HiringInbox listingId={data.id} />
      <Link data-testid="institute-referrals-link" to="/referrals" className="nb-btn nb-btn-lime mt-6">Refer & Earn · Wallet</Link>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <Users className="w-5 h-5" /> Students ({data.students.length}{!data.badge && `/${data.limits.students}`})
          </h2>
          {!data.features?.add_students && <p data-testid="students-locked" className="mt-3 text-xs nb-flat p-3 border-dashed text-slate-600" style={{ borderRadius: 10 }}>Adding students is available on paid plans.</p>}
          <form style={{ display: data.features?.add_students ? undefined : "none" }} onSubmit={(e) => { e.preventDefault(); act(() => api.post("/institute/students", student)); setStudent({ name: "", phone: "", batch: "" }); }} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input data-testid="add-student-name" required placeholder="Student name" value={student.name}
              onChange={(e) => setStudent({ ...student, name: e.target.value })} className="nb-input" />
            <input data-testid="add-student-phone" required placeholder="Phone number" value={student.phone}
              onChange={(e) => setStudent({ ...student, phone: e.target.value })} className="nb-input" />
            {data.type === "school" ? (
              <select data-testid="add-student-batch" value={student.batch}
                onChange={(e) => setStudent({ ...student, batch: e.target.value })} className="nb-input">
                <option value="">Select class</option>
                {["Nursery", "KG", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <select data-testid="add-student-batch" value={student.batch}
                onChange={(e) => setStudent({ ...student, batch: e.target.value })} className="nb-input">
                <option value="">Select batch</option>
                {data.batches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            )}
            <button data-testid="add-student-btn" className="nb-btn nb-btn-lime nb-btn-sm">Add Student</button>
          </form>
          <p className="text-xs text-slate-500 mt-2">Points are awarded after the admin verifies the student's phone number.</p>
          <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
            {data.students.map((s) => (
              <div key={s.id} data-testid={`student-row-${s.id}`} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                <span className="min-w-0">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-slate-500"> {s.phone && `· ${s.phone}`} {s.batch && `· ${s.batch}`}</span>
                  {s.admitted && <span className="text-xs text-lime-700 font-bold ml-1">Admitted</span>}
                  {s.verified
                    ? <span data-testid={`student-verified-${s.id}`} className="nb-badge ml-2" style={{ background: "#A3E635" }}>Verified</span>
                    : <span data-testid={`student-pending-${s.id}`} className="nb-badge ml-2" style={{ background: "#FCD34D" }}>Pending</span>}
                </span>
                <span className="flex gap-3">
                  {!s.admitted && (
                    <button data-testid={`admit-student-${s.id}`} title="Confirm admission" onClick={() => act(() => api.put(`/institute/students/${s.id}/admit`))}
                      className="text-lime-600 hover:text-lime-700 transition-colors duration-150"><CheckCircle2 className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`delete-student-${s.id}`} onClick={() => act(() => api.delete(`/institute/students/${s.id}`))}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> Teachers ({data.teachers.length}{!data.badge && `/${data.limits.teachers}`})
          </h2>
          {!data.features?.add_teachers && <p data-testid="teachers-locked" className="mt-3 text-xs nb-flat p-3 border-dashed text-slate-600" style={{ borderRadius: 10 }}>Adding teachers is available on paid plans.</p>}
          <p className="text-xs text-slate-500 mt-3">Search registered teachers and send a request. Manual teacher creation is no longer available.</p>
          <Link data-testid="institute-search-teacher" to="/teachers" className="nb-btn nb-btn-lime mt-3">Search Teacher</Link>
          <div className="mt-4 space-y-1 max-h-64 overflow-y-auto">
            {data.teachers.map((t) => (
              <div key={t.id} data-testid={`teacher-row-${t.id}`} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                <span className="flex items-center gap-2 min-w-0">
                  {t.photo_url ? <img src={t.photo_url} alt={t.name} className="w-7 h-7 rounded-lg object-cover border border-[#171B33]" />
                    : <span className="w-7 h-7 rounded-lg border border-[#171B33] flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: "#A3E635" }}>{t.name?.[0]}</span>}
                  <span className="font-semibold truncate">{t.name} {t.subject && <span className="text-slate-500 font-normal">· {t.subject}</span>} {t.experience && <span className="text-slate-400 font-normal">· {t.experience}</span>}</span>
                  {!t.verified && <span className="nb-badge" style={{ background: "#FCD34D", padding: "1px 6px", fontSize: 10 }}>Pending</span>}
                </span>
                <button data-testid={`delete-teacher-${t.id}`} onClick={() => act(() => api.delete(`/institute/teachers/${t.id}`))}
                  className="text-slate-400 hover:text-red-600 transition-colors duration-150 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Courses ({data.courses.length})
          </h2>
          <form onSubmit={(e) => { e.preventDefault(); act(() => api.post("/institute/courses", course)); setCourse({ title: "", fee: "", period: "Yearly" }); }} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input data-testid="add-course-title" required placeholder="Course title" value={course.title}
              onChange={(e) => setCourse({ ...course, title: e.target.value })} className="nb-input sm:col-span-2" />
            <input data-testid="add-course-fee" placeholder="Fee (₹)" value={course.fee}
              onChange={(e) => setCourse({ ...course, fee: e.target.value })} className="nb-input" />
            <select data-testid="add-course-period" value={course.period} onChange={(e) => setCourse({ ...course, period: e.target.value })} className="nb-input sm:col-span-2">
              <option>Yearly</option><option>Monthly</option><option>One-time</option>
            </select>
            <button data-testid="add-course-btn" className="nb-btn nb-btn-lime nb-btn-sm">Add Course</button>
          </form>
          <div className="mt-4 space-y-1 max-h-52 overflow-y-auto">
            {data.courses.map((c) => (
              <div key={c.id} data-testid={`course-row-${c.id}`} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                <span className="font-semibold">{c.title} <span className="text-slate-500 font-normal">{c.fee && `· ₹${c.fee}`} · {c.period}</span></span>
                <button data-testid={`delete-course-${c.id}`} onClick={() => act(() => api.delete(`/institute/courses/${c.id}`))}
                  className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <Image className="w-5 h-5" /> Gallery ({data.gallery.length})
          </h2>
          {!data.features?.gallery && <p data-testid="gallery-locked" className="mt-3 text-xs nb-flat p-3 border-dashed text-slate-600" style={{ borderRadius: 10 }}>Photo gallery is available on paid plans.</p>}
          <form style={{ display: data.features?.gallery ? undefined : "none" }} onSubmit={(e) => { e.preventDefault(); act(() => api.post("/institute/gallery", { image_url: galleryUrl })); setGalleryUrl(""); }} className="mt-4 flex gap-2">
            <input data-testid="add-gallery-url" placeholder="Image URL or upload →" value={galleryUrl}
              onChange={(e) => setGalleryUrl(e.target.value)} className="nb-input flex-1 min-w-0" />
            <UploadBtn testId="upload-gallery-image" onDone={(url) => act(() => api.post("/institute/gallery", { image_url: url }))} />
            <button data-testid="add-gallery-btn" className="nb-btn nb-btn-lime nb-btn-sm">Add</button>
          </form>
          <div className="mt-4 grid grid-cols-3 gap-3 max-h-52 overflow-y-auto">
            {data.gallery.map((g) => (
              <div key={g.id} className="relative group">
                <img src={g.image_url} alt="gallery" className="nb-flat w-full h-20 object-cover" style={{ borderRadius: 10 }} />
                <button data-testid={`delete-gallery-${g.id}`} onClick={() => act(() => api.delete(`/institute/gallery/${g.id}`))}
                  className="absolute top-1 right-1 bg-white rounded-full p-1 border-2 border-[#171B33] opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <Trash2 className="w-3 h-3 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {data.type === "coaching" && (
        <section className="mt-8 nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Batches ({data.batches.length})
          </h2>
          <form onSubmit={submitBatch} className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><label className="nb-label">Batch Name</label>
              <input data-testid="batch-name-input" required value={batch.name} onChange={(e) => setBatch({ ...batch, name: e.target.value })} className="nb-input mt-1.5" placeholder="JEE 2027 Morning Batch" /></div>
            <div><label className="nb-label">Status</label>
              <select data-testid="batch-status-select" value={batch.status} onChange={(e) => setBatch({ ...batch, status: e.target.value })} className="nb-input mt-1.5">
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
              </select></div>
            <div><label className="nb-label">Start Date</label>
              <input data-testid="batch-start-input" type="date" value={batch.start_date} onChange={(e) => setBatch({ ...batch, start_date: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">Class Timings</label>
              <input data-testid="batch-timings-input" value={batch.timings} onChange={(e) => setBatch({ ...batch, timings: e.target.value })} className="nb-input mt-1.5" placeholder="Mon-Sat, 7-9 AM" /></div>
            <div><label className="nb-label">Pricing (₹)</label>
              <input data-testid="batch-pricing-input" value={batch.pricing} onChange={(e) => setBatch({ ...batch, pricing: e.target.value })} className="nb-input mt-1.5" placeholder="45,000 / year" /></div>
            <div><label className="nb-label">Description</label>
              <input data-testid="batch-desc-input" value={batch.description} onChange={(e) => setBatch({ ...batch, description: e.target.value })} className="nb-input mt-1.5" /></div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="nb-label">Teachers in this batch</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.teachers.length === 0 && <span className="text-xs text-slate-500">Add teachers above to assign them to batches.</span>}
                {data.teachers.map((t) => {
                  const on = batch.teachers.includes(t.name);
                  return (
                    <button type="button" key={t.id} data-testid={`batch-teacher-${t.id}`}
                      onClick={() => setBatch({ ...batch, teachers: on ? batch.teachers.filter((n) => n !== t.name) : [...batch.teachers, t.name] })}
                      className="nb-badge" style={{ background: on ? "#A3E635" : "#fff", cursor: "pointer" }}>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="nb-label">Courses in this batch</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.courses.length === 0 && <span className="text-xs text-slate-500">Add courses below to link them to batches.</span>}
                {data.courses.map((c) => {
                  const on = (batch.courses || []).includes(c.title);
                  return (
                    <button type="button" key={c.id} data-testid={`batch-course-${c.id}`}
                      onClick={() => setBatch({ ...batch, courses: on ? batch.courses.filter((n) => n !== c.title) : [...(batch.courses || []), c.title] })}
                      className="nb-badge" style={{ background: on ? "#93C5FD" : "#fff", cursor: "pointer" }}>
                      {c.title}
                    </button>
                  );
                })}
              </div>
            </div>
            <button data-testid="batch-add-btn" className="nb-btn nb-btn-lime sm:col-span-2 lg:col-span-3">Add Batch</button>
          </form>
          <div className="mt-5 space-y-2">
            {data.batches.map((b) => (
              <div key={b.id} data-testid={`batch-row-${b.id}`} className="nb-flat p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2" style={{ borderRadius: 12 }}>
                <div className="text-sm">
                  <span className="font-bold">{b.name}</span>
                  <span className="nb-badge ml-2" style={{ background: b.status === "ongoing" ? "#A3E635" : "#FCD34D" }}>{b.status}</span>
                  <p className="text-slate-500 mt-1">{batchSummary(b)}</p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <button data-testid={`batch-toggle-${b.id}`} onClick={() => act(() => api.put(`/institute/batches/${b.id}`, { ...b, status: b.status === "ongoing" ? "upcoming" : "ongoing" }))}
                    className="text-xs font-bold underline underline-offset-2">Mark {b.status === "ongoing" ? "upcoming" : "ongoing"}</button>
                  <button data-testid={`batch-delete-${b.id}`} onClick={() => act(() => api.delete(`/institute/batches/${b.id}`))}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.type === "school" && schoolInfo && (
        <section className="mt-8 nb p-6">
          <h2 className="font-display text-xl font-extrabold">School Information</h2>
          <p className="text-xs text-slate-500 mt-1">Shown on your public profile — customize everything about your school.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["board", "Board (e.g. CBSE)"], ["medium", "Medium (e.g. English)"], ["classes", "Classes (e.g. Pre-nursery to 12th)"],
              ["gender", "Gender (e.g. Co-Ed)"], ["school_type", "Type (e.g. Unaided / Independent)"], ["classrooms", "No. of Classrooms"],
              ["area_sqm", "School Area (e.g. 9053 Sq. Metres)"], ["parent_institution", "Parent Institution"], ["website", "Website"],
            ].map(([k, ph]) => (
              <div key={k}>
                <label className="nb-label">{ph.split(" (")[0]}</label>
                <input data-testid={`schoolinfo-${k}-input`} value={schoolInfo[k]} placeholder={ph}
                  onChange={(e) => setSchoolInfo({ ...schoolInfo, [k]: e.target.value })} className="nb-input mt-1.5" />
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-3"><label className="nb-label">About the School</label>
              <textarea data-testid="schoolinfo-about-input" rows={3} value={schoolInfo.about}
                onChange={(e) => setSchoolInfo({ ...schoolInfo, about: e.target.value })} className="nb-input mt-1.5" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="nb-label">Facilities & Activities (comma separated)</label>
              <textarea data-testid="schoolinfo-facilities-input" rows={2} value={schoolInfo.facilitiesText} placeholder="Library, Transport, Sports Day, Labs, CCTV"
                onChange={(e) => setSchoolInfo({ ...schoolInfo, facilitiesText: e.target.value })} className="nb-input mt-1.5" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="nb-label">Fee Details (one per line: Type | Frequency | Amount)</label>
              <textarea data-testid="schoolinfo-fees-input" rows={4} value={schoolInfo.feesText} placeholder={"Admission Fee | One Time | 200\nTuition Fee | Monthly | 2090"}
                onChange={(e) => setSchoolInfo({ ...schoolInfo, feesText: e.target.value })} className="nb-input mt-1.5 font-mono text-xs" /></div>
          </div>
          <button data-testid="schoolinfo-save-btn" onClick={saveSchoolInfo} className="nb-btn nb-btn-lime nb-btn-sm mt-4">
            <Save className="w-4 h-4" /> Save School Info
          </button>
        </section>
      )}

      <section className="mt-8 nb p-6">
        <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Posts ({data.posts.length})
        </h2>
        <p className="text-xs text-slate-500 mt-1">Publish exam alerts, admissions or any update — students who follow you will see these.</p>
        <PostComposer features={data.features} postsToday={data.posts_today} onDone={load} />
        <div className="mt-4 space-y-1">
          {data.posts.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
              <span className="font-semibold min-w-0 truncate"><span className="nb-badge mr-2 capitalize" style={{ background: e.type === "mcq" ? "#C4B5FD" : e.type === "notes" ? "#93C5FD" : "#F2FBE3", padding: "1px 6px", fontSize: 10 }}>{e.type || "update"}</span>{e.title} · <span className="text-slate-500 font-normal">{(e.created_at || "").slice(0, 10)}</span></span>
              <button data-testid={`delete-post-${e.id}`} onClick={() => act(() => api.delete(`/institute/posts/${e.id}`))}
                className="text-slate-400 hover:text-red-600 transition-colors duration-150 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="nb p-6">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2">
            <Inbox className="w-5 h-5" /> Inquiries ({data.inquiries.length})
          </h2>
          <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
            {data.inquiries.length === 0 && <p className="text-sm text-slate-500">No inquiries yet.</p>}
            {data.inquiries.map((q) => (
              <div key={q.id} data-testid={`inquiry-row-${q.id}`} className="nb-flat p-4" style={{ borderRadius: 12 }}>
                <p className="font-bold text-sm">{q.name} · <span className="font-display">{q.phone}</span></p>
                {q.message && <p className="text-sm text-slate-600 mt-1">{q.message}</p>}
                {q.phone && (
                  <a href={`https://wa.me/${q.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${q.name}, thanks for your inquiry about ${data.name}!`)}`}
                    target="_blank" rel="noreferrer" data-testid={`inquiry-whatsapp-${q.id}`}
                    className="nb-btn nb-btn-sm mt-3" style={{ background: "#A3E635" }}>
                    <MessageCircle className="w-4 h-4" /> Reply on WhatsApp
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {profile && (
          <section className="nb p-6">
            <h2 className="font-display text-xl font-extrabold">Profile Settings</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="nb-label">Phone</label>
                <input data-testid="profile-phone-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">WhatsApp</label>
                <input data-testid="profile-whatsapp-input" value={profile.whatsapp} onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Since Year</label>
                <input data-testid="profile-since-input" value={profile.since_year} onChange={(e) => setProfile({ ...profile, since_year: e.target.value })} className="nb-input mt-1.5" /></div>
              <div><label className="nb-label">Banner Image</label>
                <div className="flex gap-2 mt-1.5">
                  <input data-testid="profile-banner-input" value={profile.banner_url} placeholder="URL or upload →"
                    onChange={(e) => setProfile({ ...profile, banner_url: e.target.value })} className="nb-input flex-1 min-w-0" />
                  <UploadBtn testId="upload-banner-image" onDone={(url) => setProfile((p) => ({ ...p, banner_url: url }))} />
                </div></div>
              <div className="sm:col-span-2"><label className="nb-label">Exams / courses you prepare for (comma separated — shows in Leaderboard filters)</label>
                <input data-testid="profile-exams-input" value={profile.examsText} placeholder="e.g. JEE, NEET, UPSC, Class 10 Boards" onChange={(e) => setProfile({ ...profile, examsText: e.target.value })} className="nb-input mt-1.5" /></div>
              <div className="sm:col-span-2"><label className="nb-label">Description</label>
                <textarea data-testid="profile-description-input" rows={2} value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} className="nb-input mt-1.5" /></div>
              <div className="sm:col-span-2">
                <label className="nb-label">Social media profiles (shown to students & parents instead of your email)</label>
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.keys(profile.social_links).map((k) => (
                    <input key={k} data-testid={`profile-social-${k}`} placeholder={`${k.charAt(0).toUpperCase() + k.slice(1)} URL`} value={profile.social_links[k]}
                      onChange={(e) => setProfile({ ...profile, social_links: { ...profile.social_links, [k]: e.target.value } })} className="nb-input" />
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="profile-save-btn" onClick={() => { const { examsText, ...rest } = profile; act(() => api.put("/institute/profile", { ...rest, exams: examsText.split(",").map((s) => s.trim()).filter(Boolean) })); }} className="nb-btn nb-btn-lime nb-btn-sm mt-4">
              <Save className="w-4 h-4" /> Save Profile
            </button>
          </section>
        )}
      </div>

      <AccountActions />
    </div>
  );
}
